import React, { useState, useEffect } from 'react';
import { Stack, TextField, MenuItem } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../../../app/hooks';
import { 
  useUpsertRuleMutation, 
  useDeleteRuleMutation, 
  useGetMedicareContractsQuery,
  contractApi 
} from '../../../../../services/ContractService';
import { RuleCommands } from './RuleCommands';

interface RateBasedOnFeeScheduleProps {
  ruleId: string;
  ruleString?: string;
  ruleDescription?: string;
}

interface RateBasedOnFeeScheduleRule {
  contract: string;
  feeScheduleCategory: string;
  rate: number;
}

const RateBasedOnFeeSchedule: React.FC<RateBasedOnFeeScheduleProps> = ({
  ruleId,
  ruleString,
  ruleDescription,
}) => {
  const [searchParams] = useSearchParams();
  const contractId = searchParams.get('id');
  const company = searchParams.get('company');
  const dispatch = useAppDispatch();
  const [rule, setRule] = useState<RateBasedOnFeeScheduleRule>({ 
    contract: '', 
    feeScheduleCategory: '', 
    rate: 0 
  });
  
  const { data: medicareContracts = [] } = useGetMedicareContractsQuery({ company: company || 'Default' });
  const [upsertRule] = useUpsertRuleMutation();
  const [deleteRule] = useDeleteRuleMutation();

  useEffect(() => {
    if (ruleString && ruleString !== '') {
      try {
        const parsedRule: RateBasedOnFeeScheduleRule = JSON.parse(ruleString);
        setRule(parsedRule);
      } catch (error) {
        console.error('Failed to parse rule string:', error);
      }
    }
  }, [ruleString, ruleId]);

  const save = async () => {
    if (!contractId) return;

    try {
      const ruleData = {
        contractId: contractId,
        ruleId: ruleId === '0' ? '' : ruleId,
        ruleType: 'RateBasedOnFeeSchedule',
        priority: 0,
        ruleString: JSON.stringify(rule),
        description: ruleDescription ?? '',
      };

      await upsertRule(ruleData).unwrap();
      dispatch(contractApi.util.invalidateTags(['RuleList']));
    } catch (error) {
      console.error('Failed to save rule:', error);
    }
  };

  const ruleDelete = async () => {
    if (ruleId === '0') return;

    try {
      await deleteRule({ ruleId }).unwrap();
      dispatch(contractApi.util.invalidateTags(['RuleList']));
    } catch (error) {
      console.error('Failed to delete rule:', error);
    }
  };

  return (
    <React.Fragment>
      <Stack direction="row" gap={1} sx={{ mb: 2 }}>
        <TextField
          label="Contract"
          select
          size="small"
          sx={{ width: '250px' }}
          value={rule.contract}
          onChange={(e) => setRule({ ...rule, contract: e.target.value })}
        >
          <MenuItem value="">Unknown</MenuItem>
          {medicareContracts.map((contract: any) => (
            <MenuItem key={contract.id} value={contract.id}>
              {contract.payer}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Fee Schedule Category"
          value={rule.feeScheduleCategory}
          onChange={(e) => setRule({ ...rule, feeScheduleCategory: e.target.value })}
          sx={{ width: '200px' }}
        />
        <TextField
          type="number"
          label="Rate"
          value={rule.rate}
          onChange={(e) => setRule({ ...rule, rate: Number(e.target.value) })}
          sx={{ width: '100px' }}
          inputProps={{ step: 0.01 }}
        />
      </Stack>
      <RuleCommands
        saveCommand={save}
        deleteCommand={ruleDelete}
        isNewRule={ruleId === '0'}
      />
    </React.Fragment>
  );
};

export { RateBasedOnFeeSchedule };